// activeWorkspaceRecord.js — the ONE owner of what may land in `root.workspace`
// (tickets/spec-workspace-record-shape.md, 2026-08-08).
//
// THE DEFECT this closes: `root.workspace` was populated from two incompatible
// shapes and the code freely substituted one for the other —
//
//   sdk.getWorkspace(id) / the /core/boot `workspace` section  → FULL record
//     (`_id`, `organization`, `displayName`, `settings`, …) — `settings`
//     carries `navbar`, `workspaceApps`, `home_default_panels`, `featureFlags`,
//     `appAccessControl`.
//   claims.workspaces / listWorkspaces rows                    → LIST entry
//     (`id`, `name`, `slug`, `type`, `org_id`, `role`) — no `settings`, ever.
//
// When a list entry landed in `root.workspace`, every `workspace.settings.*`
// read silently resolved to `undefined` and fell back to framework defaults —
// the workspace still showed the right NAME, so it looked loaded. Measured
// live on dev.my.symbols.app: a cold `/add-app` load raced auth.js's claims
// tick against bootShell's full-record fetch and lost, painting the default
// navbar over a fully-configured workspace. It is a RACE, not a route bug —
// any route can lose it on any auth tick.
//
// THE RULE: `root.workspace` holds a COMPLETE workspace record, or it holds
// nothing. A list/claims-shaped entry is never assigned to it directly — it
// goes through `setActiveWorkspaceRecord`, which enforces monotonic shape
// (a list entry can never DOWNGRADE a full record already held for the same
// id) and guarantees the full record gets fetched once any id is known, on
// ANY resolution path — not just the two branches bootShell used to gate on
// `activeWorkspaceId` being present at BOOT time.
//
// Provenance stamp: every workspace record/row gets a non-destructive
// `_shape: 'full' | 'list'` marker — same precedent as the workspace-switch
// isolation invariants' provenance stamps (see MEMORY
// project_workspace_switch_isolation_invariants.md). `root.workspaceReady`
// mirrors "is `root.workspace` currently `_shape:'full'`" so consumers can
// gate rendering on it explicitly instead of inferring readiness from
// `settings` being non-empty (a workspace can legitimately have NO settings
// overrides — that must not read as "not loaded").

import { isLoudDevRuntime } from './isLoudDevRuntime.js'

// Normalize the server's raw shape (`_id` / `displayName` / `organization`)
// to what every consumer reads (`id` / `name` / `org_id`). Mutates + returns
// the same object — callers that need an immutable stamp should shallow-clone
// first (see `stampWorkspaceShape`'s `clone` option).
export const normalizeWorkspaceRecord = (ws) => {
  if (!ws || typeof ws !== 'object') return ws
  if (!ws.id && ws._id) ws.id = String(ws._id)
  if (!ws.name && ws.displayName) ws.name = ws.displayName
  if (!ws.org_id && ws.organization) ws.org_id = String(ws.organization)
  return ws
}

// Stamp the provenance marker. `clone: true` (default) returns a NEW object
// so a shared reference — a JWT claims row, a cached list entry other code
// still reads — is never mutated out from under its other readers.
export const stampWorkspaceShape = (ws, shape, { clone = true } = {}) => {
  if (!ws || typeof ws !== 'object') return ws
  const out = clone ? { ...ws } : ws
  out._shape = shape
  return out
}

export const isFullWorkspaceRecord = (ws) => !!ws && ws._shape === 'full'

// Stamp every row of a list (claims.workspaces / listWorkspaces rows) as
// `list`-shaped. Never overwrites an existing `_shape` (a row that already
// carries a stamp — e.g. re-processed on a later tick — keeps it).
export const stampWorkspaceListShape = (rows) =>
  (Array.isArray(rows) ? rows : []).map((w) =>
    w && typeof w === 'object' ? { ...w, _shape: w._shape || 'list' } : w
  )

// THE workspace name. `displayName` is the schema field and the ONLY one the
// server writes: `PATCH /workspaces/:id` allowlists `displayName` (never
// `name`), and `WorkspaceMemberService.rename` writes it through a strict
// `findByIdAndUpdate`, which drops an off-schema key. `name` is a legacy
// field that still sits on older Mongo documents and is FROZEN at whatever it
// held when the field was retired — for most of those rows, the literal string
// "Default".
//
// So the precedence must be `displayName` FIRST. Reading `name` first means a
// workspace rename is written correctly and then never displayed: measured on
// my.symbols.app 2026-09-04, 8 of the account's 23 workspaces printed
// "Default" in the switcher while their `displayName` held the real name
// (TMR Intranet, Astio AI, AIHouse Intranet, Bigbrother, Mankanet, Freestyler,
// Community). Every one of the 23 rows carried a `displayName`, so the
// fallback below is only for a row shape that predates the field entirely.
//
// ONE resolver on purpose. This rule was written out at three call sites in
// two packages and drifted into two different orders — bootShell/auth/
// AppOrgSwitcher read `name` first, pageContext/marketplace/loadPrivateData
// read `displayName` first. Call this; do not re-derive it.
export const workspaceDisplayName = (ws) => (ws && (ws.displayName || ws.name)) || ''

// Fetch the full record for `id` and stamp it. Pure — no state mutation.
// `getWorkspaceCached` is DI'd (mirrors bootShell's existing pattern) so this
// module never has to pick a fetch-coalescing strategy: bootShell passes its
// own DI'd coalescer, auth.js passes `./getWorkspaceCached.js`'s singleton,
// and a caller with neither still gets a correct (uncoalesced) fetch.
export const fetchFullWorkspaceRecord = async (sdk, id, { getWorkspaceCached } = {}) => {
  if (!sdk || !id) return null
  try {
    let raw
    if (typeof getWorkspaceCached === 'function') {
      raw = await getWorkspaceCached(sdk, id)
    } else if (typeof sdk.getWorkspace === 'function') {
      const res = await sdk.getWorkspace(id)
      raw = res?.data || res || null
    } else {
      return null
    }
    if (!raw || !(raw._id || raw.id)) return null
    // FA204 / frank trap — self-contained inline copies of
    // normalizeWorkspaceRecord/stampWorkspaceShape (a bare sibling-export
    // call is exactly as unresolvable post-frank-serialization as a
    // module-local read; the two named exports stay live for their many
    // real external consumers — bootShell.js/loadPrivateData.js/auth.js/
    // switchWorkspace.js/this file's own test suite).
    const ws = { ...raw }
    if (!ws.id && ws._id) ws.id = String(ws._id)
    if (!ws.name && ws.displayName) ws.name = ws.displayName
    if (!ws.org_id && ws.organization) ws.org_id = String(ws.organization)
    ws._shape = 'full'
    return ws
  } catch (err) {
    console.warn('[activeWorkspaceRecord] fetchFullWorkspaceRecord failed:', err?.message || err)
    return null
  }
}

// ── §6 — late arrival must RE-RENDER ─────────────────────────────────────────
// Fetching the full record is only half the fix. `root.workspace` landing does
// NOT, by itself, repaint anything that derives from it:
//
//   • the fetch plugin has no reactive re-run — a `skip: true` evaluated at
//     mount is permanent;
//   • an async `root.update` does not re-fire prop factories on its own;
//   • and the navbar specifically does not read `workspace.settings.navbar` at
//     all — it reads `root.workspaceSettings.navbar`, a DERIVED bag published
//     only by workspaceConfigs.js's applyWorkspaceConfig. Nothing re-runs that
//     when the record it resolves FROM arrives afterwards.
//
// That last one is the whole observed defect: on a lean boot applyWorkspaceConfig
// runs early, resolves through to DEFAULT_CONFIG because `settings` is absent,
// and publishes the bare default navbar. The full record then lands — and no
// re-resolve is ever triggered, so the default navbar stays painted over a
// workspace that has a configured one. (applyWorkspaceConfig already knows how
// to correct itself once re-run: its `savedNavbarIsBareDefault` guard exists so
// a previously-published bare default loses to a preset that carries a real
// navbar. It was only ever missing the trigger.)
//
// So the setter publishes an UPGRADE event and the workspace package subscribes
// its activation chain to it. The dependency direction is deliberate: `shared`
// owns the event, the consumer package owns the reaction — shared must not
// import workspace/functions/workspaceConfigs.js.
// FA204 / frank trap — `_fullRecordListeners`/`_pendingFullFetches` used to
// live at module scope (genuine cross-call state: a Set of subscriber fns
// and an in-flight-fetch dedup set, both shared across every independent
// caller of the exports below) and `_markCompletionUnreachable` was a bare
// module-local helper. Neither export below takes an `el`/`root`-bearing
// argument that could carry this on `el.scope` (root is a caller-supplied
// arg, not a stable owner across calls) so both re-home onto
// `globalThis.__activeWorkspaceRecordListeners` /
// `globalThis.__activeWorkspaceRecordPendingFetches` per the
// mwtFlag.js/pointerCapability.js template — each export below carries its
// own self-contained lazy-init of the same global rather than a shared
// module-scope getter (which would just be the same trap one level down).
// `globalThis`, not `window`, because this file's own test suite runs
// under plain `node:test` with no DOM.

// Subscribe to "the active workspace just became a FULL record". Called with
// `(root, workspace)`. Returns an unsubscribe. Idempotent per function
// identity (Set semantics), so a boot path that registers on every pass —
// which subscribeAuthSession does — never stacks duplicate reactions.
export const onActiveWorkspaceFull = (fn) => {
  if (!globalThis.__activeWorkspaceRecordListeners) {
    globalThis.__activeWorkspaceRecordListeners = new Set()
  }
  const listeners = globalThis.__activeWorkspaceRecordListeners
  if (typeof fn !== 'function') return () => {}
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// Exported for tests only — lets a test assert the transition semantics
// (fires on list→full and on a NEW id's full record; does NOT fire on a
// full→full no-op re-assert of the same id) without a live shell.
export const _notifyActiveWorkspaceFull = (root, ws) => {
  const listeners = globalThis.__activeWorkspaceRecordListeners
  if (!listeners) return
  for (const fn of listeners) {
    try {
      fn(root, ws)
    } catch (err) {
      console.warn('[activeWorkspaceRecord] full-record listener failed:', err?.message || err)
    }
  }
}

// ── boot identity correction ────────────────────────────────────────────────
// WS-COLD-BOOT-IDENTITY-CORRECTION-KEEPS-PROVISIONAL-WORKSPACE-ROWS-1. The
// shell can adopt a PROVISIONAL workspace at boot — a stored pointer or a
// claim that has not yet converged with the active org — and correct it a
// moment later while `loadPrivateData`'s sign-in fan-out is still in flight
// (workspace/functions/auth.js's `_loadInFlight` guard). That guard
// deliberately skips the full `onWorkspaceChange` re-hydration (it would
// double every in-flight boot fetch), so nothing else re-runs a BUILT-IN
// page's mount-time read: History, /admin/logs and /admin/usage each read
// ONCE from `onCreate` and only listen on `onActiveWorkspaceFull` when they
// had NO id at all yet — a read that already succeeded against the
// provisional id never learns that id was wrong.
//
// A SEPARATE channel from `onActiveWorkspaceFull` on purpose, not a reuse.
// A DELIBERATE switch (loadPrivateData.js `onWorkspaceChange`) is also a
// full-record identity change and already fires `onActiveWorkspaceFull` —
// but it re-hydrates its own mounted surfaces explicitly, in the same pass
// (its PHASE 2). A surface that ALSO reloaded on that notification would read
// History (etc.) twice per deliberate switch. Only auth.js's boot
// self-correction fires this one — never a fully-formed switch.
export const onWorkspaceIdentityCorrected = (fn) => {
  if (!globalThis.__workspaceIdentityCorrectedListeners) {
    globalThis.__workspaceIdentityCorrectedListeners = new Set()
  }
  const listeners = globalThis.__workspaceIdentityCorrectedListeners
  if (typeof fn !== 'function') return () => {}
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// Fired once per boot self-correction, from auth.js alone. `ws` is whatever
// shape the auth tick resolved at that moment (list or full) — every
// listener re-derives its own scope from `root` rather than trusting this
// argument, the same discipline `onActiveWorkspaceFull` listeners already
// follow.
export const notifyWorkspaceIdentityCorrected = (root, ws) => {
  const listeners = globalThis.__workspaceIdentityCorrectedListeners
  if (!listeners) return
  for (const fn of listeners) {
    try {
      fn(root, ws)
    } catch (err) {
      console.warn(
        '[activeWorkspaceRecord] identity-correction listener failed:',
        err?.message || err
      )
    }
  }
}

// THE single guarded setter for `root.workspace` (+ the paired
// `root.workspaceReady` flag) on a LIVE (post-mount) root. Pre-mount seeding
// (bootShell, before `create()` exists) writes `context.state` directly —
// there's no prior value to protect there, just stamp + seed (see bootShell.js
// for that path); this setter is for every write AFTER mount.
//
// Mechanism (spec §4b):
//   full  replacing anything            → accept
//   list  replacing nothing             → accept, schedule the full fetch
//   list  replacing full, SAME id       → REJECT (the bug this closes)
//   list  replacing full, DIFFERENT id  → accept as provisional identity,
//                                          not-ready, fetch immediately
//
// `opts.sdk` is required for the auto-fetch-scheduling side effect (omit it
// to accept-without-fetching — e.g. a caller that will fetch explicitly
// itself). `opts.getWorkspaceCached` is optional DI, forwarded to
// `fetchFullWorkspaceRecord`. `opts.reason` is a short breadcrumb for the
// rejected-downgrade dev warning.
export const setActiveWorkspaceRecord = (root, next, opts = {}) => {
  // FA204 / frank trap — self-contained inline copies of
  // _notifyActiveWorkspaceFull (reading the same
  // globalThis.__activeWorkspaceRecordListeners set the exported version
  // reads)/fetchFullWorkspaceRecord (a bare sibling-export call is exactly
  // as unresolvable post-frank-serialization as a module-local read; the
  // named export stays live for its own test suite) and
  // _markCompletionUnreachable (was a bare module-local helper, now local
  // to this export, its sole caller). `_pendingFullFetches` is genuine
  // cross-call dedup state (must survive BETWEEN separate top-level calls
  // to this function, not just within one call) — re-homed onto
  // `globalThis.__activeWorkspaceRecordPendingFetches`.
  const notifyActiveWorkspaceFull = (r, ws) => {
    const listeners = globalThis.__activeWorkspaceRecordListeners
    if (!listeners) return
    for (const fn of listeners) {
      try {
        fn(r, ws)
      } catch (err) {
        console.warn('[activeWorkspaceRecord] full-record listener failed:', err?.message || err)
      }
    }
  }
  const fetchFullRecord = async (sdk, id, { getWorkspaceCached } = {}) => {
    if (!sdk || !id) return null
    try {
      let raw
      if (typeof getWorkspaceCached === 'function') {
        raw = await getWorkspaceCached(sdk, id)
      } else if (typeof sdk.getWorkspace === 'function') {
        const res = await sdk.getWorkspace(id)
        raw = res?.data || res || null
      } else {
        return null
      }
      if (!raw || !(raw._id || raw.id)) return null
      const ws = { ...raw }
      if (!ws.id && ws._id) ws.id = String(ws._id)
      if (!ws.name && ws.displayName) ws.name = ws.displayName
      if (!ws.org_id && ws.organization) ws.org_id = String(ws.organization)
      ws._shape = 'full'
      return ws
    } catch (err) {
      console.warn('[activeWorkspaceRecord] fetchFullWorkspaceRecord failed:', err?.message || err)
      return null
    }
  }
  // Terminal marker for "this record will never become full". Written onto
  // the held record itself (not a sibling root key) so it travels with the
  // record and is automatically void the moment a different workspace is
  // adopted — exactly like `_shape`. Idempotent, and never applied to a
  // record that has meanwhile become full or moved to another id.
  const markCompletionUnreachable = (r, id) => {
    const cur = r && r.workspace
    if (!cur || cur._shape === 'full' || cur._completionUnreachable) return
    if (String(cur.id || cur._id || '') !== String(id)) return
    if (isLoudDevRuntime()) {
      console.warn(
        `[activeWorkspaceRecord] workspace ${id} could not be completed to a full record`
      )
    }
    r.update({ workspace: { ...cur, _completionUnreachable: true } })
  }
  if (!globalThis.__activeWorkspaceRecordPendingFetches) {
    globalThis.__activeWorkspaceRecordPendingFetches = new Set()
  }
  const pendingFullFetches = globalThis.__activeWorkspaceRecordPendingFetches

  if (!root) return false
  const reason = opts.reason || 'unknown'

  if (!next) {
    // Explicit clear — sign-out / no active workspace. Always allowed; there
    // is nothing to protect a clear from.
    root.update({ workspace: null, workspaceReady: false })
    return true
  }

  const shape = next._shape === 'full' ? 'full' : 'list'
  const nextId = String(next.id || next._id || '')
  const current = root.workspace
  const currentShape = current ? (current._shape === 'full' ? 'full' : 'list') : null
  const currentId = current ? String(current.id || current._id || '') : ''

  if (shape === 'list' && currentShape === 'full' && currentId && currentId === nextId) {
    if (isLoudDevRuntime()) {
      console.warn(
        `[activeWorkspaceRecord] rejected list-shape downgrade of full workspace ${nextId} (reason: ${reason})`
      )
    }
    return false
  }

  // A full-record refresh can change language without changing identity.
  // Capture the pair before update: reactive stores may merge the new record
  // into the held object. Missing policy and explicit 'default' are equivalent.
  const languageChanged =
    (current?.settings?.language || '') !== (next.settings?.language || '') ||
    (current?.settings?.languagePolicy || 'default') !==
      (next.settings?.languagePolicy || 'default')

  root.update({ workspace: next, workspaceReady: shape === 'full' })

  // §6 — announce the UPGRADE, not every write. A transition is
  // (nothing | list) → full, or a full record for a DIFFERENT id than the one
  // previously held. A changed language/policy pair also needs convergence:
  // the shell's language listener otherwise never sees an admin's update to
  // the active workspace. Identical pairs stay silent so repeated full-record
  // fetches cannot put the activation chain on a hot path.
  if (shape === 'full' && (!(currentShape === 'full' && currentId === nextId) || languageChanged)) {
    notifyActiveWorkspaceFull(root, next)
  }

  // §4c — guaranteed completion on ANY resolution path, not just the two
  // bootShell branches gated on `activeWorkspaceId` being present at boot.
  // Whenever what ends up held is list-shaped, converge to the full record.
  if (shape === 'list' && nextId) {
    if (!opts.sdk) {
      // Nobody is going to complete this one — the caller handed no sdk, so
      // there is no fetch to wait for. Say so TERMINALLY (see
      // `_completionUnreachable` below): a render site that gates on "still
      // loading" must be able to tell "wait for it" from "this is all there
      // is", or a caller that never schedules a fetch leaves that gate hung
      // forever and the surface renders its indeterminate state permanently.
      markCompletionUnreachable(root, nextId)
    } else if (!pendingFullFetches.has(nextId)) {
      pendingFullFetches.add(nextId)
      Promise.resolve(
        fetchFullRecord(opts.sdk, nextId, { getWorkspaceCached: opts.getWorkspaceCached })
      )
        .then((full) => {
          // Staleness guard: only apply if the tab is still scoped to the SAME
          // workspace id this fetch was for — a switch that happened while the
          // fetch was in flight must not have its new scope stomped by a late
          // answer for the OLD id.
          const cur = root.workspace
          const curId = cur ? String(cur.id || cur._id || '') : ''
          if (curId !== nextId) return
          if (full) {
            setActiveWorkspaceRecord(root, full, { reason: `${reason}:full-fetch-followup` })
            return
          }
          // The fetch ran and came back with nothing (network failure, 403,
          // an sdk without getWorkspace). Dropping that on the floor is the
          // silent-catch this whole spec exists to end: readiness would sit
          // false forever with no way for a consumer to distinguish it from
          // "any moment now".
          markCompletionUnreachable(root, nextId)
        })
        .finally(() => pendingFullFetches.delete(nextId))
    }
  }

  return true
}
